import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors, fonts, spacing } from '../constants';
import { useAuth } from '../context/AuthContext';
import { getRoleDisplayName } from '../utils/roleUtils';
import { useSettingsPageState } from '../hooks/pages/settings/useSettingsPageState';
import { useSettingsPageData } from '../hooks/pages/settings/useSettingsPageData';
import { useSettingsProfile } from '../hooks/pages/settings/useSettingsProfile';
import { useSettingsPassword } from '../hooks/pages/settings/useSettingsPassword';
import { useSettingsNotifications } from '../hooks/pages/settings/useSettingsNotifications';
import ProfileSection from '../components/settingsPage/ProfileSection';
import PasswordSection from '../components/settingsPage/PasswordSection';
import NotificationSection from '../components/settingsPage/NotificationSection';

const Settings = () => {
  const { user, updateUser } = useAuth();
  const roleDisplayName = getRoleDisplayName(user?.role);

  // Initialize all hooks
  const pageState = useSettingsPageState(user);

  useSettingsPageData({
    user,
    setNotificationSettings: pageState.setNotificationSettings,
    setIsLoadingPreferences: pageState.setIsLoadingPreferences,
  });

  const { handleSaveProfile } = useSettingsProfile({
    profileData: pageState.profileData,
    user,
    updateUser,
    setIsSavingProfile: pageState.setIsSavingProfile,
    setIsEditingProfile: pageState.setIsEditingProfile,
  });

  const { handleChangePassword, handleClearPassword } = useSettingsPassword({
    passwordData: pageState.passwordData,
    setPasswordData: pageState.setPasswordData,
    setShowPasswords: pageState.setShowPasswords,
    setIsChangingPassword: pageState.setIsChangingPassword,
  });

  const { handleNotificationToggle } = useSettingsNotifications({
    notificationSettings: pageState.notificationSettings,
    setNotificationSettings: pageState.setNotificationSettings,
    setIsSavingEmailNotification: pageState.setIsSavingEmailNotification,
  });

  // Handlers
  const handleEditToggle = () => {
    pageState.setIsEditingProfile(true);
  };

  const handleCancelProfile = () => {
    pageState.resetProfileData();
    pageState.setIsEditingProfile(false);
  };

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: colors.background,
          width: '100%',
          p: { xs: spacing.sm, sm: spacing.md },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 'none' }}>
          {/* Header */}
          <Box sx={{ mb: spacing.md, mt: 0 }}>
            <Typography
              variant="body1"
              sx={{
                color: colors.textSecondary,
                fontSize: { xs: fonts.fontSizeSm, sm: fonts.fontSizeMd },
                mb: spacing.sm,
              }}
            >
              Manage your account settings and preferences
            </Typography>
          </Box>

          {/* Profile Section */}
          <ProfileSection
            user={user}
            profileData={pageState.profileData}
            isEditingProfile={pageState.isEditingProfile}
            isSavingProfile={pageState.isSavingProfile}
            onProfileChange={pageState.handleProfileChange}
            onEditToggle={handleEditToggle}
            onSave={handleSaveProfile}
            onCancel={handleCancelProfile}
            roleDisplayName={roleDisplayName}
          />

          {/* Password Update Section */}
          <PasswordSection
            passwordData={pageState.passwordData}
            showPasswords={pageState.showPasswords}
            isChangingPassword={pageState.isChangingPassword}
            onPasswordChange={pageState.handlePasswordChange}
            onToggleVisibility={pageState.togglePasswordVisibility}
            onChangePassword={handleChangePassword}
            onClear={handleClearPassword}
          />

          {/* Notification Settings */}
          <NotificationSection
            notificationSettings={pageState.notificationSettings}
            isSavingEmailNotification={pageState.isSavingEmailNotification}
            isLoadingPreferences={pageState.isLoadingPreferences}
            onNotificationToggle={handleNotificationToggle}
          />
        </Box>
      </Box>
    </>
  );
};

export default Settings;
